/**
 * Canvas Actions — Phase 2
 * Bridge between layout computations and store updates.
 */

import { useCanvasStore, type ItemType } from '@/stores/canvasStore';
import {
  computeGridLayout,
  computeHorizontalLayout,
  computeVerticalLayout,
  computeRadialLayout,
  computeClusterLayout,
} from './layoutAlgorithms';
import { getViewportCenter } from './viewportHelpers';

export type LayoutType =
  | 'grid'
  | 'horizontal_row'
  | 'vertical_column'
  | 'radial'
  | 'cluster';

/**
 * Apply layout results to existing items by updating their positions.
 */
export async function applyLayout(
  positions: Map<string, { x: number; y: number }>,
  options: { animate?: boolean } = { animate: true }
): Promise<void> {
  const store = useCanvasStore.getState();

  // If animate, enable the auto-organizing CSS transitions
  // (isAutoOrganizing is set directly on the store state)
  if (options.animate) {
    useCanvasStore.setState({ isAutoOrganizing: true });
  }

  const updatePromises: Promise<void>[] = [];
  positions.forEach((pos, itemId) => {
    updatePromises.push(
      store.updateItem(itemId, {
        position_x: pos.x,
        position_y: pos.y,
      })
    );
  });

  await Promise.all(updatePromises);

  // Turn off transition mode after a delay
  if (options.animate) {
    setTimeout(() => {
      useCanvasStore.setState({ isAutoOrganizing: false });
    }, 600);
  }
}

/**
 * Create multiple items with computed placements in one operation.
 * Returns created item IDs for follow-up operations (like adding connections).
 */
export async function createItemsWithLayout(
  items: Array<{
    itemType: ItemType;
    content: Record<string, unknown>;
    width?: number;
    height?: number;
  }>,
  layoutType: LayoutType,
  options: {
    centerX?: number;
    centerY?: number;
    animate?: boolean;
  } = {}
): Promise<string[]> {
  const store = useCanvasStore.getState();
  const { panX, panY, zoom } = store;

  const centerX = options.centerX ?? getViewportCenter({ panX, panY, zoom }).x;
  const centerY = options.centerY ?? getViewportCenter({ panX, panY, zoom }).y;

  // Assign default sizes
  const sized = items.map((item, i) => ({
    id: `temp-${i}`,
    width: item.width ?? getDefaultWidth(item.itemType),
    height: item.height ?? getDefaultHeight(item.itemType),
  }));

  // Compute layout
  let positions: Map<string, { x: number; y: number }>;
  switch (layoutType) {
    case 'grid':
      positions = computeGridLayout(sized, { startX: centerX - 300, startY: centerY - 200 });
      break;
    case 'horizontal_row':
      positions = computeHorizontalLayout(sized, { startX: centerX - 400, startY: centerY });
      break;
    case 'vertical_column':
      positions = computeVerticalLayout(sized, { startX: centerX, startY: centerY - 300 });
      break;
    case 'radial':
      if (sized.length > 1) {
        positions = computeRadialLayout(sized[0], sized.slice(1), { centerX, centerY });
      } else {
        positions = computeGridLayout(sized, { startX: centerX, startY: centerY });
      }
      break;
    case 'cluster':
      positions = computeClusterLayout(sized, { centerX, centerY });
      break;
    default:
      positions = computeGridLayout(sized, { startX: centerX, startY: centerY });
  }

  // Create items at computed positions
  const createdIds: string[] = [];
  for (let i = 0; i < items.length; i++) {
    const pos = positions.get(`temp-${i}`);
    if (!pos) continue;

    const created = await store.createItem({
      item_type: items[i].itemType,
      content: items[i].content,
      position_x: pos.x,
      position_y: pos.y,
      width: items[i].width ?? getDefaultWidth(items[i].itemType),
      height: items[i].height ?? getDefaultHeight(items[i].itemType),
    });

    if (created) {
      createdIds.push(created.id);
    }
  }

  return createdIds;
}

export function getDefaultWidth(itemType: string): number {
  const defaults: Record<string, number> = {
    sticky: 200,
    text_card: 280,
    document: 320,
    image: 280,
    link: 280,
    board_link: 200,
  };
  return defaults[itemType] ?? 200;
}

export function getDefaultHeight(itemType: string): number {
  const defaults: Record<string, number> = {
    sticky: 200,
    text_card: 180,
    document: 240,
    image: 200,
    link: 160,
    board_link: 100,
  };
  return defaults[itemType] ?? 150;
}
